/*
 * Copyright (C) 2025 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { convertForDataTest, EMPTY_STRING, isNotUndefined } from '@common/tech-shared';
import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { cva, VariantProps } from 'class-variance-authority';
import { uniqueId } from 'lodash-es';

const selectVariants = cva(
  'flex items-center w-full rounded-sm border bg-background-50 px-3 py-2 focus:outline-solid focus:outline-2 focus:outline-offset-2',
  {
    variants: {
      variant: {
        default: 'border-primary/50 focus:outline-focus focus:border-background-200 hover:border-primary-hover',
        error: 'border-error/50 hover:border-error focus:outline-error focus:border-background-200',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);
type SelectVariants = VariantProps<typeof selectVariants>;

@Component({
  selector: 'ods-select',
  imports: [CommonModule, ReactiveFormsModule],
  host: { class: 'flex flex-col w-full gap-2 text-text' },
  styleUrl: './select.component.scss',
  template: `<label [attr.for]="selectId" class="text-md text-text block font-medium">{{ selectLabel }}</label>
    <select
      [attr.data-test-id]="_dataTestId + '-select'"
      [attr.id]="selectId"
      [ngClass]="selectVariants({ variant })"
      [formControl]="fieldControl"
      [attr.aria-describedby]="ariaDescribedBy"
      [attr.aria-invalid]="variant === 'error' ? true : null"
    >
      <ng-content />
    </select>`,
})
export class SelectComponent {
  @Input({ required: true }) set label(label: string) {
    this.selectLabel = label;
    this.selectId = uniqueId(convertForDataTest(label) + '-');
    if (!this._dataTestId) this._dataTestId = convertForDataTest(this.selectLabel);
  }
  @Input() fieldControl: FormControl = new FormControl(EMPTY_STRING);
  @Input() variant: SelectVariants['variant'];
  @Input() ariaDescribedBy: string = null;
  @Input() set dataTestId(value: string) {
    if (isNotUndefined(value)) this._dataTestId = value;
  }

  public selectLabel: string;
  public selectId: string;
  public _dataTestId: string;
  public readonly selectVariants = selectVariants;
}
